import { useEffect, useMemo, useState } from 'react';

import BalancedEquationLevelModal from './balancedEquationLevelModal';
import PrinciplesLevelModal from './principlesLevelModal';
import ClassificationLevelModal from './classificationLevelModal';
import NormalBalanceLevelModal from './normalBalanceLevelModal';
import Alert from '../../../alert';

import './tournamentEditPanel.css';

// Canonical game type list rendered in Step 1's exercise picker. The
// short label is the visible card title; the label matches the enum on
// generalInfo.games[].game_type used to look up existing tournament
// rounds and pick the right level modal.
const GAME_TYPE_CARDS = [
  { key: 'classification', label: 'Account Classification', short: 'Classification' },
  { key: 'normal',         label: 'Normal Balance',         short: 'Normal Balance' },
  { key: 'equation',       label: 'Balanced Equation',      short: 'Balanced Equation' },
  { key: 'principles',     label: 'The Principles',         short: 'The Principles' },
];

// March Madness bracket names for round labels, Championship-last.
// slice(-N) yields the last N so a 2-round session lands as
// ['Final 4', 'Championship']. Kept in sync with the create-flow
// naming in edit-tournament.js — do not reorder.
const BRACKET_ROUND_NAMES = ['Sweet 16', 'Elite 8', 'Final 4', 'Championship'];

// Which per-game-type array on a scenario-level record actually stores
// the questions. Every level ships with all four fields regardless of
// game type — the ones that don't apply come back as [], so falling
// through with ?? lands on the wrong (empty) field. Pick explicitly
// by game type.
const QUESTIONS_FIELD_BY_GAME_TYPE = {
  'Account Classification': 'questions',
  'Normal Balance': 'normal_questions',
  'Balanced Equation': 'be_questions',
  'The Principles': 'transactions',
};

function getLevelQuestions(level, gameType) {
  const field = QUESTIONS_FIELD_BY_GAME_TYPE[gameType];
  if (!field) return [];
  return level?.[field] || [];
}

// Compact summary line rendered inside each level pill. Length is
// read off the game-type-specific field so BE / Principles don't get
// counted against the (always-empty) `questions` array.
function formatLevelPillLabel(level, gameType) {
  const questionCount = getLevelQuestions(level, gameType).length;
  const levelNumber = level?.level ?? '?';
  const noun = questionCount === 1 ? 'question' : 'questions';
  return `Level ${levelNumber} · ${questionCount} ${noun}`;
}

// Per-game-type ceiling on how many questions a student actually
// answers in one game session. Kept in sync with the `gameQuestionsLimit`
// constants each game module owns (Classification/data.js, NormalBalance/
// data.js, BalancedEquation/data.js, ThePrinciples/data.js) — update
// alongside them.
const GAME_QUESTIONS_LIMIT_BY_GAME_TYPE = {
  'Account Classification': 15,
  'Normal Balance': 15,
  'Balanced Equation': 10,
  'The Principles': 10,
};

// Tooltip copy for the pill label. Cites the real per-game ceiling
// (`num_decisions_question` when the level template overrides it,
// otherwise the game type's built-in `gameQuestionsLimit`) so the
// message reflects what students actually see — not the pool size.
function formatLevelPillTooltip(level, gameType) {
  const poolSize = getLevelQuestions(level, gameType).length;
  const override = level?.num_decisions_question;
  const baseCap = GAME_QUESTIONS_LIMIT_BY_GAME_TYPE[gameType];
  const rawCap = (typeof override === 'number' && override > 0) ? override : baseCap;
  const effectiveCap = typeof rawCap === 'number' ? Math.min(rawCap, poolSize) : poolSize;

  if (effectiveCap && effectiveCap < poolSize) {
    return `Pool of ${poolSize} possible questions. Students see ${effectiveCap} per game (picked at random).`;
  }
  return `Pool of ${poolSize} possible questions. Students see all ${poolSize} per game.`;
}

// Extract the human-readable list of questions rendered inside the
// view-popup on hover. Reads from the game-type-specific field so
// classification / normal show account-item names and BE / principles
// show transaction descriptions.
function extractQuestionsForView(level, gameType) {
  const items = getLevelQuestions(level, gameType);
  if (gameType === 'Balanced Equation' || gameType === 'The Principles') {
    return items.map(item => item.description).filter(Boolean);
  }
  return items.map(item => item.account_item?.name).filter(Boolean);
}

function ExercisePickerCard({ card, roundLabel, isSelected, isLocked, onToggle }) {
  const isInteractive = !isLocked && !!onToggle;
  const classes = [
    'tournament-edit-exercise-card',
    isSelected ? 'is-selected' : 'is-unselected',
    isLocked ? 'is-locked' : '',
    isInteractive ? 'is-clickable' : '',
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    if (!isInteractive) return;
    onToggle(card.key);
  };

  const handleKeyDown = (e) => {
    if (!isInteractive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(card.key);
    }
  };

  return (
    <div
      className={classes}
      title={isLocked ? 'Add or remove exercises from Assignments' : ''}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-pressed={isInteractive ? isSelected : undefined}
    >
      <div className="tournament-edit-exercise-card-header">
        <span className={`tournament-edit-exercise-check ${isSelected ? 'checked' : ''}`} aria-hidden="true" />
        {isSelected && roundLabel && (
          <span className="tournament-edit-round-badge">{roundLabel}</span>
        )}
      </div>
      <div className="tournament-edit-exercise-card-body">
        <div className="tournament-edit-exercise-card-title">{card.short}</div>
        {!isSelected && <div className="tournament-edit-exercise-card-sub">Not included</div>}
      </div>
    </div>
  );
}

function RoundCard({ round, roundLabel, scenarioLevels, isLoading, onAddLevel, onEditLevel, onDeleteLevel, isGameStarted }) {
  const hasLevels = !!scenarioLevels?.length;
  const gameTypeShort = round.gameType === 'Account Classification'
    ? 'Account Classification'
    : round.gameType === 'The Principles'
      ? 'The Principles'
      : round.gameType;

  return (
    <div className="tournament-edit-round-row">
      <div className="tournament-edit-round-row-label">
        <span className="tournament-edit-round-badge">{roundLabel || round.name || `Round ${round.number}`}</span>
      </div>
      <div className={`tournament-edit-round-card ${hasLevels ? 'is-valid' : 'is-invalid'}`}>
      <div className="tournament-edit-round-header">
        <span className="tournament-edit-round-drag" aria-hidden="true" />
        <h3 className="tournament-edit-round-title">{gameTypeShort}</h3>
        <span
          className={`tournament-edit-round-status ${hasLevels ? 'ok' : 'warn'}`}
          aria-label={hasLevels ? 'Valid' : 'Needs at least one level'}
        />
      </div>

      <div className="tournament-edit-round-body">
        {isLoading && <div className="tournament-edit-loading">Loading levels…</div>}

        {!isLoading && hasLevels && (
          <>
            <span className="tournament-edit-round-label">Levels</span>
            <div className="tournament-edit-level-pills">
              {scenarioLevels.map((level, index) => {
                const viewItems = extractQuestionsForView(level, round.gameType);
                return (
                  <div key={level.id} className="tournament-edit-level-pill">
                    <span className="tournament-edit-level-pill-body-wrap">
                      <span className="tournament-edit-level-pill-body">
                        {formatLevelPillLabel(level, round.gameType)}
                      </span>
                      <span className="tournament-edit-level-pill-body-tooltip" role="tooltip">
                        {formatLevelPillTooltip(level, round.gameType)}
                      </span>
                    </span>
                    <div className="tournament-edit-level-pill-view-wrap">
                      <button
                        type="button"
                        className="tournament-edit-level-pill-icon"
                        aria-label="View questions in this level"
                        title="View questions"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <div className="tournament-edit-level-pill-view-popup" role="tooltip">
                        <div className="tournament-edit-level-pill-view-popup-title">
                          {formatLevelPillLabel(level, round.gameType)}
                        </div>
                        {viewItems.length ? (
                          <ol className="tournament-edit-level-pill-view-popup-list">
                            {viewItems.map((text, i) => (
                              <li key={`${level.id}-${i}`}>{text}</li>
                            ))}
                          </ol>
                        ) : (
                          <div className="tournament-edit-level-pill-view-popup-empty">
                            No questions selected yet.
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="tournament-edit-level-pill-icon"
                      onClick={() => onEditLevel(round, index)}
                      disabled={isGameStarted}
                      aria-label="Edit level"
                      title={isGameStarted ? 'Assignment is locked — students have started' : 'Edit level'}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="tournament-edit-level-pill-delete"
                      onClick={() => onDeleteLevel(round, level.id)}
                      disabled={isGameStarted}
                      aria-label="Delete level"
                      title={isGameStarted ? 'Assignment is locked — students have started' : 'Delete level'}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                className="tournament-edit-add-level"
                onClick={() => onAddLevel(round)}
                disabled={isGameStarted}
                title={isGameStarted ? 'Assignment is locked — students have started' : 'Add a level'}
              >
                <span aria-hidden="true">+</span> Add
              </button>
            </div>
          </>
        )}

        {!isLoading && !hasLevels && (
          <div className="tournament-edit-round-empty-row">
            <span className="tournament-edit-round-empty-copy">
              Add questions to this exercise.
            </span>
            <button
              type="button"
              className="tournament-edit-add-level tournament-edit-add-level--primary"
              onClick={() => onAddLevel(round)}
              disabled={isGameStarted}
              title={isGameStarted ? 'Assignment is locked — students have started' : 'Add a level'}
            >
              <span aria-hidden="true">+</span> Add
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default function TournamentEditPanel({
  assignmentName,
  onAssignmentNameChange,
  onAssignmentNameSave,
  isAssignmentNameSaving,
  roundGames,
  scenarioLevelsByRound,
  loadingByRound,
  isGameStartedByGame,
  editingContext,
  filterItems,
  filterItemsDesign,
  editingScenarioLevelPreCheckedItems,
  businessType,
  isModalLoading,
  onAddLevel,
  onEditLevel,
  onDeleteLevel,
  onCloseModal,
  onSaveLevelSelection,
  // Create mode: no tournament exists yet. Step 1 becomes interactive,
  // Steps 2 & 3 are hidden, and the primary CTA below Step 1 fires the
  // create flow (tournament + games + round-setup) instead of any
  // per-level modal work.
  isCreateMode = false,
  selectedGameKeys,
  onToggleGame,
  onCreateSubmit,
  isCreateSubmitting = false,
  createErrorMessage = '',
  tournamentId = null,
  // Review-Session-only: when the instructor adds the first level to a
  // Classification / Normal round, the level modal surfaces an extra
  // "The Elements" card that auto-creates class-elements / norm-elements
  // via this callback (no per-question picker step).
  onElementsPresetSelected,
}) {
  // Local state for the assignment-name input so it stays responsive
  // while the parent debounces or waits for the Save click. The parent
  // owns the canonical value via assignmentName.
  const [nameDraft, setNameDraft] = useState(assignmentName || '');
  // Keep the draft in sync when the parent value changes (e.g. after
  // a save round-trip completes and the API response overwrites state).
  useEffect(() => setNameDraft(assignmentName || ''), [assignmentName]);

  const [deletePendingRound, setDeletePendingRound] = useState(null);
  const [deletePendingLevelId, setDeletePendingLevelId] = useState(null);

  const nameIsDirty = nameDraft !== (assignmentName || '');

  // For Step 1: which game types are already assigned to rounds, and
  // what round-name label should render on the selected card. In edit
  // mode the label comes from round.name (set by the tournament setup/
  // endpoint, e.g. "Championship"); in create mode there are no rounds
  // yet so we derive labels from the bracket-name tail based on how many
  // exercises are currently checked — matches the naming used by the
  // create flow in edit-tournament.js.
  const roundLabelByKey = useMemo(() => {
    const map = {};
    GAME_TYPE_CARDS.forEach(card => { map[card.key] = null; });
    if (isCreateMode) {
      const selected = (selectedGameKeys || []).filter(k => GAME_TYPE_CARDS.some(c => c.key === k));
      const orderedSelected = GAME_TYPE_CARDS
        .filter(c => selected.includes(c.key))
        .map(c => c.key);
      const names = BRACKET_ROUND_NAMES.slice(-orderedSelected.length);
      orderedSelected.forEach((key, idx) => { map[key] = names[idx]; });
      return map;
    }
    (roundGames || []).forEach(round => {
      const card = GAME_TYPE_CARDS.find(c => c.label === round.gameType);
      if (card && map[card.key] == null) {
        map[card.key] = round.name || `Round ${round.number}`;
      }
    });
    return map;
  }, [isCreateMode, selectedGameKeys, roundGames]);

  const createSelectedCount = (selectedGameKeys || []).length;
  const canSubmitCreate = isCreateMode
    && createSelectedCount > 0
    && !!nameDraft.trim()
    && !isCreateSubmitting;

  const handleDeleteConfirm = () => {
    if (deletePendingRound && deletePendingLevelId != null) {
      onDeleteLevel(deletePendingRound, deletePendingLevelId);
    }
    setDeletePendingRound(null);
    setDeletePendingLevelId(null);
  };

  const handleDeleteCancel = () => {
    setDeletePendingRound(null);
    setDeletePendingLevelId(null);
  };

  const handleDeleteRequest = (round, levelId) => {
    setDeletePendingRound(round);
    setDeletePendingLevelId(levelId);
  };

  // Elements preset is only offered when creating the FIRST level on a
  // Classification / Normal round in a tournament (Review Session) —
  // both modals accept the prop but only show the card when true.
  const showElementsPresetCard = !!editingContext
    && editingContext.editingScenarioLevelIndex === null
    && (scenarioLevelsByRound?.[editingContext.round.id]?.length || 0) === 0;

  const sharedModalProps = editingContext ? {
    scenarioLevelNumber:
      typeof editingContext.editingScenarioLevelIndex === 'number'
        ? editingContext.editingScenarioLevelIndex + 1
        : ((scenarioLevelsByRound?.[editingContext.round.id]?.length || 0) + 1),
    onCloseButtonClick: onCloseModal,
    onSaveButtonClick: onSaveLevelSelection,
    isButtonLoading: isModalLoading,
    filterItems,
    decisionType: scenarioLevelsByRound?.[editingContext.round.id]?.[editingContext.editingScenarioLevelIndex]?.name,
    editingScenarioLevelPreCheckedItems,
  } : null;

  return (
    <div className="tournament-edit-panel">
      <div className="tournament-edit-name-row">
        <label className="tournament-edit-name-label" htmlFor="tournament-edit-name-input">Assignment name</label>
        <input
          id="tournament-edit-name-input"
          type="text"
          className="tournament-edit-name-input"
          value={nameDraft}
          onChange={(e) => {
            setNameDraft(e.target.value);
            onAssignmentNameChange?.(e.target.value);
          }}
          maxLength={50}
        />
        <span className="tournament-edit-name-counter" aria-live="polite">
          {nameDraft.length}/50
        </span>
        {!isCreateMode && (
          <button
            type="button"
            className="tournament-edit-name-save"
            onClick={() => onAssignmentNameSave?.(nameDraft)}
            disabled={!nameIsDirty || isAssignmentNameSaving || !nameDraft.trim()}
          >
            {isAssignmentNameSaving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>

      <hr className="tournament-edit-divider" />

      <section className="tournament-edit-section">
        <header className="tournament-edit-section-header">
          <span className="tournament-edit-step-badge">Step 1</span>
          <div className="tournament-edit-section-heading">
            <h2>Choose exercises</h2>
            <p>Select the exercise(s) for the review session.</p>
          </div>
        </header>

        <div className="tournament-edit-exercise-grid">
          {GAME_TYPE_CARDS.map(card => (
            <ExercisePickerCard
              key={card.key}
              card={card}
              roundLabel={roundLabelByKey[card.key]}
              isSelected={roundLabelByKey[card.key] != null}
              isLocked={!isCreateMode}
              onToggle={isCreateMode ? onToggleGame : undefined}
            />
          ))}
        </div>

        {isCreateMode && (
          <>
            <div className="tournament-edit-create-cta">
              <button
                type="button"
                className="tournament-edit-create-submit"
                onClick={() => onCreateSubmit?.(nameDraft)}
                disabled={!canSubmitCreate}
              >
                {isCreateSubmitting ? 'Creating…' : 'Create Review Session'}
              </button>
              {!isCreateSubmitting && createSelectedCount === 0 && (
                <span className="tournament-edit-create-hint">Select at least one exercise to continue.</span>
              )}
            </div>
            {createErrorMessage && (
              <div className="tournament-edit-create-error" role="alert">
                Something went wrong while creating the review session: {createErrorMessage}. Check the browser console for details, then try again.
              </div>
            )}
          </>
        )}
      </section>

      {!isCreateMode && <hr className="tournament-edit-divider" />}

      {!isCreateMode && (
        <section className="tournament-edit-section">
          <header className="tournament-edit-section-header">
            <span className="tournament-edit-step-badge">Steps 2 & 3</span>
            <div className="tournament-edit-section-heading">
              <h2>Order the rounds and add level questions</h2>
              <p>Each exercise is assigned to a round. You can re-assign the exercises to any round at any time before launch.</p>
            </div>
          </header>

          <div className="tournament-edit-rounds-list">
            {(roundGames || []).map(round => {
              const card = GAME_TYPE_CARDS.find(c => c.label === round.gameType);
              const roundLabel = card
                ? roundLabelByKey[card.key]
                : (round.name || `Round ${round.number}`);
              return (
                <RoundCard
                  key={round.id}
                  round={round}
                  roundLabel={roundLabel}
                  scenarioLevels={scenarioLevelsByRound?.[round.id]}
                  isLoading={loadingByRound?.[round.id]}
                  onAddLevel={onAddLevel}
                  onEditLevel={onEditLevel}
                  onDeleteLevel={handleDeleteRequest}
                  isGameStarted={isGameStartedByGame?.[round.game_id] || false}
                />
              );
            })}
          </div>
        </section>
      )}

      {!isCreateMode && tournamentId && <hr className="tournament-edit-divider" />}

      {!isCreateMode && tournamentId && (() => {
        // Gate the tournament-view button on every round having at least
        // one level attached — a round without levels can't be played,
        // so previewing the bracket would just surface the broken state.
        const allRoundsHaveLevel = (roundGames || []).length > 0
          && (roundGames || []).every(r => (scenarioLevelsByRound?.[r.id]?.length || 0) > 0);
        return (
          <section className="tournament-edit-section">
            <header className="tournament-edit-section-header">
              <span className="tournament-edit-step-badge">Step 4</span>
              <div className="tournament-edit-section-heading">
                <h2>View tournament structure</h2>
                <p>Open the review-session bracket in a new tab to see how the rounds will play out.</p>
              </div>
            </header>
            <div className="tournament-edit-view-tournament-row">
              <button
                type="button"
                className="tournament-edit-view-tournament"
                onClick={() => window.open(`/instructor/review-session/${tournamentId}`, '_blank')}
                disabled={!allRoundsHaveLevel}
                title={allRoundsHaveLevel ? 'Open the tournament bracket in a new tab' : 'Add at least one level to every exercise to view the tournament'}
              >
                View Tournament
              </button>
              {!allRoundsHaveLevel && (
                <span className="tournament-edit-view-tournament-hint">
                  Add at least one level to every exercise to enable this.
                </span>
              )}
            </div>
          </section>
        );
      })()}

      {editingContext && editingContext.round.gameType === 'Balanced Equation' && (
        <BalancedEquationLevelModal {...sharedModalProps} />
      )}
      {editingContext && editingContext.round.gameType === 'The Principles' && (
        <PrinciplesLevelModal {...sharedModalProps} />
      )}
      {editingContext && editingContext.round.gameType === 'Account Classification' && (
        <ClassificationLevelModal
          {...sharedModalProps}
          businessType={businessType}
          showElementsPresetCard={showElementsPresetCard}
          onElementsPresetSelected={() => onElementsPresetSelected?.(editingContext.round)}
        />
      )}
      {editingContext && editingContext.round.gameType === 'Normal Balance' && (
        <NormalBalanceLevelModal
          {...sharedModalProps}
          showElementsPresetCard={showElementsPresetCard}
          onElementsPresetSelected={() => onElementsPresetSelected?.(editingContext.round)}
        />
      )}

      {deletePendingRound && (
        <Alert
          type="warning"
          title="Are you sure you want to delete this level?"
          confirmButtonText="Yes"
          cancelButtonText="No"
          onCancelButtonClick={handleDeleteCancel}
          onConfirmButtonClick={handleDeleteConfirm}
        />
      )}
    </div>
  );
}
